import {
  Button,
  createStyles,
  Modal,
  Theme,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslations } from "adapters/i18n";
import React, { type ComponentType, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import type { RootState } from "redux/reducers";
import { DATE_FORMATS, DateUtils } from "shared/dateUtils";
import {
  getNewsRegistersFromStorage,
  setNewsRegistersInStorage,
} from "shared/storageUtils";
import type { PortalNews } from "shared/types";
import { Show } from "../Show";
import { NEWS_IMAGE_PATH } from "./newsModal.constants";
import { shouldDisplayNews } from "./newsModal.utils";

type StateProps = ReturnType<typeof mapStateToProps>;
type OwnProps = { news: PortalNews[] };
type NewsModalProps = OwnProps & StateProps & WithStyles<typeof styles>;

function NewsModalImpl({ classes, news, farmId, user }: NewsModalProps) {
  const { t } = useTranslations();

  const [isOpen, setIsOpen] = useState(false);
  const [newsDisplay, setNewsDisplay] = useState<PortalNews | null>(null);

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (user?.Usuario == null || farmId == null) {
      setIsOpen(false);
      return;
    }

    const registeredNews = getNewsRegistersFromStorage(user.Usuario.Login);
    const newsToDisplay = news.filter((item) =>
      shouldDisplayNews(item, registeredNews?.[item.id] ?? null)
    );

    if (newsToDisplay.length === 0) {
      setIsOpen(false);
      return;
    }

    const target = newsToDisplay[newsToDisplay.length - 1];
    setNewsRegistersInStorage(
      {
        lastView: DateUtils.now().format(DATE_FORMATS["YYYY-MM-DD"]),
        countViews: (registeredNews?.[target.id]?.countViews ?? 0) + 1,
      },
      target.id,
      user.Usuario.Login
    );

    setNewsDisplay(target);
    setIsOpen(true);
  }, [farmId, news, user.Usuario]);

  const modalTitle = newsDisplay?.title ?? t("news:we-have-news");
  const showLink = newsDisplay?.link != null;

  return (
    <Modal
      className={classes.root}
      data-testid="news-modal"
      disableBackdropClick={true}
      disableEnforceFocus={true}
      onClose={handleCloseModal}
      open={isOpen}
    >
      <div className={classes.contentWrapper}>
        <CloseIcon className={classes.closeIcon} onClick={handleCloseModal} />
        <Typography className={classes.modalTitle} variant="h2">
          {modalTitle}
        </Typography>

        <Show when={newsDisplay?.showImage !== false}>
          <img
            src={NEWS_IMAGE_PATH}
            alt={t("news:news")}
            className={classes.modalImage}
          />
        </Show>

        <div className={classes.modalContent}>{newsDisplay?.message}</div>

        <Button
          className={classes.actionButton}
          color="primary"
          component={showLink ? "a" : "button"}
          data-testid="news-modal-action-button"
          href={showLink ? newsDisplay.link : undefined}
          onClick={showLink ? undefined : handleCloseModal}
          variant="contained"
        >
          {showLink ? t("news:check-news") : t("common:actions.i-understood")}
        </Button>
      </div>
    </Modal>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      placeContent: "center",
    },
    contentWrapper: {
      alignItems: "center",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
      display: "flex",
      flexDirection: "column",
      fontFamily: "Roboto",
      gap: "20px",
      height: "fit-content",
      justifyContent: "space-between",
      margin: "auto",
      maxHeight: "90vh",
      minHeight: "532px",
      padding: "16px",
      position: "relative",
      width: "clamp(min(578px,90%), 578px, 90%)",
      "&:focus-visible": {
        outline: "none",
      },
    },
    closeIcon: {
      alignSelf: "end",
      cursor: "pointer",
      position: "absolute",
    },
    modalTitle: {
      color: theme.palette.primary.main,
      flexGrow: 0,
      fontFamily: "inherit",
      fontSize: "24px",
      fontWeight: "bold",
      letterSpacing: "0.44px",
      lineHeight: "1.17",
      marginTop: "20px",
      textAlign: "center",
      width: "100%",
    },
    modalImage: {
      flexGrow: 0,
      height: "136px",
      width: "136px",
    },
    modalContent: {
      color: "#666",
      display: "block",
      fontFamily: "inherit",
      fontSize: "16px",
      height: "100%",
      lineHeight: "1.33",
      overflowY: "auto",
      padding: "0 20px",
      width: "100%",
    },
    actionButton: {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "18px",
      fontFamily: "inherit",
      flexGrow: 0,
      fontSize: "14px",
      margin: "0px auto",
      width: "fit-content",
    },
  });

const mapStateToProps = (state: RootState) => ({
  user: state.usuario,
  farmId: state.fazenda.FazendaSelecionada,
});

const NewsModal = compose(
  withStyles(styles),
  connect(mapStateToProps)
)(NewsModalImpl) as ComponentType<OwnProps>;

export default NewsModal;
